/**
 * TrustPersonDialog — Trust-group entry point for the RAO people form.
 *
 * Analogue of IndividualPersonDialog for the Trust branch. Composes:
 *   - TrustIdentitySection (trust name, sub-type picker, tax ID, agreement
 *     date, governing-law state, trustee count)
 *   - TrustCipSection (Core Director CIP registration fields 254/255/256/
 *     257/258 + checklist of required trust docs)
 *   - Shared AddressSection, ContactSection, DocumentSlotsSection
 *
 * OMITS vs Individual:
 *   - Citizenship section (trusts aren't citizens)
 *   - Employment section (entityGroupConfig.showEmployment === false)
 *   - IndividualCipScanSection (trusts have no photo IDs; trustees' photo IDs
 *     are captured in their own Individual dialogs)
 *
 * Mirrors IndividualPersonDialog's architecture:
 *   - useTrustDerivation drives customerType + sub-type flags
 *   - TrustIdentitySection publishes the derived customerType back via onChange
 *   - attachedDocs is lifted here and passed to both CIP (read-only checklist)
 *     and DocumentSlotsSection (upload surface + ingestion)
 *   - Supports 'readOnly' prop; always a modal (inline 'existing-cif-maint'
 *     surface was retired 2026-04-24 — Maint CIF is a modal too).
 *
 * Estate sub-type is NOT handled here — per people-form-port.md:392 the
 * dispatcher routes Estate into its own EstatePersonDialog. This dialog's
 * sub-type dropdown is limited to the 4 Trust-branch types.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { TrustIdentitySection } from './TrustIdentitySection';
import { TrustCipSection } from './TrustCipSection';
import { useTrustDerivation } from './useTrustDerivation';

// Shared sections
import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { DocumentSlotsSection } from '../shared/DocumentSlotsSection';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import { EntityConvergedShell } from '../EntityConvergedShell';
import type { PdwTabKey } from '../PdwTabStrip';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

/** Parity with IndividualPersonDialogMode — modal-always. */
export type TrustPersonDialogMode = 'review-add' | 'new' | 'edit';

export interface TrustPersonDialogProps {
  open: boolean;
  mode: TrustPersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required by DocumentSlotsSection for per-cart document attachment. */
  cartId: string;
  /** Officer code for upload metadata. Defaults to 'UNKNOWN'. */
  officerCode?: string;
  /** When true, disables all inputs + hides edit/save controls. */
  readOnly?: boolean;
  /** Early Core search (PeopleStep) — the "No EIN / TIN" proof searches by the entity name. */
  onEarlyDupCheck?: (draft: RaoPerson, markNoTinCleared?: (key: string) => void) => void;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function TrustPersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
  onEarlyDupCheck,
}: TrustPersonDialogProps): JSX.Element | null {
  const [local, setLocal] = useState<RaoPerson>(person);

  // "No EIN / TIN" proof (Bill 2026-10-14): once the box is ticked and the entity name is in, search
  // Core by that name; no match stamps noTinClearedKey (the Review gate then waives the tax ID).
  const lastEntityCheckRef = useRef<string>('');
  useEffect(() => {
    if (!onEarlyDupCheck || readOnly || mode !== 'new' || local.cifId || local.tinCode !== 'N') return;
    const name = (local.name2 ?? '').trim().toLowerCase();
    if (!name || lastEntityCheckRef.current === name) return;
    lastEntityCheckRef.current = name;
    onEarlyDupCheck(local, (cleared) => setLocal((l) => ({ ...l, noTinClearedKey: cleared })));
  }, [local, onEarlyDupCheck, readOnly, mode]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Slot-4 T0 (Contract C2) — attachedDocs live on the REAL draft
  // (local.extra.attachedDocs); TrustCipSection reads them as a Hard-doc
  // checklist and they now survive save + reopen.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode doc hydration from the v3 Document Library.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  // Normalize mode for section-level props.
  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';

  // Read stored sub-type off the existing CIF so the derivation hook can
  // surface a promotion banner when the officer flips sub-type on reload.
  const storedSubType =
    typeof person.extra?.trustSubType === 'string'
      ? (person.extra.trustSubType as string)
      : undefined;

  const derivation = useTrustDerivation(local, {
    mode: sectionMode,
    storedSubType,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...patch };
      // Keep fullName in sync — trust display name = trust legal name
      // (firstName / name1), optionally joined with secondary name2.
      const primary = (next.firstName ?? '').trim();
      const secondary = (next.name2 ?? '').trim();
      const display = [primary, secondary].filter(Boolean).join(' / ').trim();
      if (display) next.fullName = display;
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const isChanged = useCallback(
    (field: string): boolean => {
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  const displayName =
    (local.firstName ?? '').trim() ||
    (local.name2 ?? '').trim() ||
    '[Unnamed Trust]';

  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add new trust'
        : `Edit trust — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  // Save-disable surfaces: duplicate CIF already on cart + supervisor gate.
  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  const saveDisabled = dupInCart || !!derivation.requiresSupervisorApproval;

  // BACKLOG-0016 — sticky-footer Return/Continue dyad (flat-scroll dialog).
  const continueDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : derivation.requiresSupervisorApproval
      ? 'Supervisor approval required before saving.'
      : undefined;

  // ---------------------------------------------------------------------------
  // Sections grouped by canonical tab (D3 convergence). Trust surfaces:
  // Profile (trust name / sub-type / agreement) · CIP (CD 254-258 + Hard-doc
  // checklist; Trust-Cert upload-or-generate lives here) · Contact · Documents.
  // No personal photo-ID CIP — trustees carry their own Individual dialogs.
  // ---------------------------------------------------------------------------
  const sectionsByTab: Partial<Record<PdwTabKey, JSX.Element>> = {
    profile: (
      <TrustIdentitySection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        derivation={derivation}
        readOnly={readOnly}
      />
    ),
    cip: (
      <TrustCipSection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        readOnly={readOnly}
        attachedDocs={attachedDocs}
      />
    ),
    contact: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
        <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
      </div>
    ),
    documents: (
      <DocumentSlotsSection
        person={local}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        entityType={derivation.customerType}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
  };

  // Modal surface — every entry into the dialog (New Acct + Maint CIF).
  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <EntityConvergedShell
        person={local}
        mode={mode}
        dupInCart={dupInCart}
        tabs={['review', 'profile', 'cip', 'contact', 'documents']}
        sectionsByTab={sectionsByTab}
        onAttachedDocsChange={(docs) => docs.forEach(registerAttachedDoc)}
        onPersonPatch={update}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        readOnly={readOnly}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onClose}
        onContinue={() => onSave(local)}
        continueDisabled={saveDisabled || readOnly}
        continueDisabledReason={continueDisabledReason}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
